/**
 * The in-app connect flow for an agent CLI's SUBSCRIPTION account.
 *
 * One component, two flow shapes, because the two CLIs sanction different
 * OAuth paths — and the shape is what the user sees, so it is the only thing
 * that varies here:
 *
 *   'paste'  (Claude Code) — the backend runs `claude setup-token` in a pty,
 *            we open the claude.ai authorize URL in a new tab, and the user
 *            pastes back the authorization code claude.ai shows them.
 *
 *   'device' (Codex) — the backend runs `codex login --device-auth`, we open
 *            OpenAI's device page and show the one-time code; the user enters
 *            it THERE, so there is nothing to paste back and we simply poll
 *            until the backend has captured the credential.
 *
 * Everything around that middle step is shared: the connected badge, reconnect
 * and disconnect, the status meta, the toasts.
 *
 * Shared by BOTH connect surfaces, which differ only in the endpoints they hit
 * and where the credential lands:
 *   - Settings → LLM (`AgentCredentialPanel`): the user's own tenant/personal
 *     subscription.
 *   - Instance-admin console → AI Settings: the PLATFORM-default subscription
 *     every user riding the platform default runs on.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { CheckCircle2, Copy, ExternalLink, Loader2, Unplug } from 'lucide-react'
import toast from 'react-hot-toast'
import type { AgentConnectStatus } from '@/api/settings'

/** Claude: start → open tab → user pastes the code back here → complete. */
export interface PasteConnectApi {
  kind: 'paste'
  start: () => Promise<{ flow_id: string; auth_url: string }>
  complete: (
    flowId: string,
    code: string,
  ) => Promise<{ ok: boolean; validated: boolean; status: AgentConnectStatus }>
  cancel: (flowId: string) => Promise<void>
  status: () => Promise<AgentConnectStatus>
  disconnect: () => Promise<void>
}

/** Codex: start → open tab + show the code → poll until the CLI has the token. */
export interface DeviceConnectApi {
  kind: 'device'
  start: () => Promise<{
    flow_id: string
    verification_uri: string
    user_code: string
    expires_at: string
  }>
  poll: (flowId: string) => Promise<{ state: 'pending' | 'connected'; status?: AgentConnectStatus }>
  cancel: (flowId: string) => Promise<void>
  status: () => Promise<AgentConnectStatus>
  disconnect: () => Promise<void>
}

export type AgentConnectApi = PasteConnectApi | DeviceConnectApi

interface ActiveFlow {
  flow_id: string
  /** Where the user signs in — the authorize URL, or the device page. */
  url: string
  /** Device flow only: the one-time code the user types on that page. */
  user_code?: string
  expires_at?: string
}

interface Props {
  /** Display name of the account being connected ("Claude", "Codex"). */
  provider: string
  /** Whether a credential is already stored (badge vs connect button). */
  isSet: boolean
  api: AgentConnectApi
  /** Changing this refetches the connection meta (e.g. the scope toggle). */
  statusKey?: string
  /** Notifies the parent that the stored credential changed server-side. */
  onStored: (isSet: boolean) => void
  /** Footer note explaining where the connection is saved. */
  footer?: ReactNode
  /** Confirmation shown before disconnecting. */
  disconnectConfirm?: string
}

/** How often we ask the backend whether the device code has been approved. */
const DEVICE_POLL_MS = 2000

export default function AgentConnectSection({
  provider,
  isSet,
  api,
  statusKey = '',
  onStored,
  footer,
  disconnectConfirm,
}: Props) {
  const [status, setStatus] = useState<AgentConnectStatus | null>(null)
  const [flow, setFlow] = useState<ActiveFlow | null>(null)
  const [code, setCode] = useState('')
  const [starting, setStarting] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [copied, setCopied] = useState(false)
  // Guards the poll loop against a flow the user cancelled mid-request.
  const activeFlowRef = useRef<string | null>(null)

  // Connection meta (connected_at/expires_at/account) — refreshed on scope change.
  useEffect(() => {
    let cancelled = false
    api
      .status()
      .then((s) => {
        if (!cancelled) setStatus(s)
      })
      .catch(() => {
        /* meta is decorative — the masked bool still drives the badge */
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusKey, isSet])

  // The poll effect must survive re-renders: the parent recreates `onStored`
  // (and therefore any callback closing over it) on every render, so depending
  // on one directly would tear the timer down and restart it each time — and if
  // anything re-rendered faster than the poll interval, the poll would never
  // actually fire. Keep them in refs and key the effect on the flow id alone.
  const finishRef = useRef<(s: AgentConnectStatus | undefined, message: string) => void>(() => {})
  finishRef.current = (s, message) => {
    activeFlowRef.current = null
    if (s) setStatus(s)
    setFlow(null)
    setCode('')
    onStored(true)
    toast.success(message)
  }
  const pollRef = useRef<DeviceConnectApi['poll'] | null>(null)
  pollRef.current = api.kind === 'device' ? api.poll : null

  // ---- Device flow: poll until the user approves on the provider's page ----
  const deviceFlowId = api.kind === 'device' ? flow?.flow_id ?? '' : ''
  useEffect(() => {
    if (!deviceFlowId) return
    let stopped = false
    let timer = 0

    const tick = async () => {
      if (stopped || activeFlowRef.current !== deviceFlowId) return
      try {
        const res = await pollRef.current?.(deviceFlowId)
        if (stopped || activeFlowRef.current !== deviceFlowId) return
        if (res?.state === 'connected') {
          finishRef.current(res.status, `${provider} account connected`)
          return
        }
      } catch (e: any) {
        if (stopped || activeFlowRef.current !== deviceFlowId) return
        // A failed poll is terminal — the backend only errors once the flow
        // itself has failed, expired or been replaced.
        activeFlowRef.current = null
        setFlow(null)
        toast.error(e?.response?.data?.detail || e?.message || 'The sign-in did not complete')
        return
      }
      if (!stopped) timer = window.setTimeout(tick, DEVICE_POLL_MS)
    }

    timer = window.setTimeout(tick, DEVICE_POLL_MS)
    return () => {
      stopped = true
      window.clearTimeout(timer)
    }
  }, [deviceFlowId, provider])

  async function handleConnect() {
    setStarting(true)
    try {
      let next: ActiveFlow
      if (api.kind === 'device') {
        const f = await api.start()
        next = {
          flow_id: f.flow_id,
          url: f.verification_uri,
          user_code: f.user_code,
          expires_at: f.expires_at,
        }
      } else {
        const f = await api.start()
        next = { flow_id: f.flow_id, url: f.auth_url }
      }
      activeFlowRef.current = next.flow_id
      setFlow(next)
      setCode('')
      setCopied(false)
      // Open the provider's sign-in in a new tab; the box below also links it
      // in case the popup was blocked.
      window.open(next.url, '_blank', 'noopener')
    } catch (e: any) {
      toast.error(
        e?.response?.data?.detail || e?.message || `Could not start the ${provider} connect flow`,
      )
    } finally {
      setStarting(false)
    }
  }

  /** Paste flow only. */
  async function handleComplete() {
    if (!flow || api.kind !== 'paste') return
    if (!code.trim()) {
      toast.error(`Paste the authorization code shown by ${provider}`)
      return
    }
    setCompleting(true)
    try {
      const res = await api.complete(flow.flow_id, code.trim())
      activeFlowRef.current = null
      setStatus(res.status)
      setFlow(null)
      setCode('')
      onStored(true)
      if (res.validated === false) {
        // Stored, but the backend's API ping could not verify it — usually a
        // mangled capture or an account without API access.
        toast(
          `${provider} account connected, but the token could not be verified against the API — run a test agent task to confirm it works.`,
          { icon: '⚠️', duration: 8000 },
        )
      } else {
        toast.success(`${provider} account connected`)
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not complete the connection')
    } finally {
      setCompleting(false)
    }
  }

  function handleCancel() {
    if (flow) {
      api.cancel(flow.flow_id).catch(() => {})
    }
    activeFlowRef.current = null
    setFlow(null)
    setCode('')
  }

  async function handleDisconnect() {
    const message =
      disconnectConfirm ||
      `Disconnect this ${provider} account? Agent runs using the subscription will stop working until you reconnect.`
    if (!confirm(message)) return
    setDisconnecting(true)
    try {
      await api.disconnect()
      setStatus({ connected: false })
      onStored(false)
      toast.success(`${provider} account disconnected`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not disconnect')
    } finally {
      setDisconnecting(false)
    }
  }

  async function copyCode() {
    if (!flow?.user_code) return
    try {
      await navigator.clipboard.writeText(flow.user_code)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Could not copy — select the code and copy it manually')
    }
  }

  const expires = status?.expires_at ? new Date(status.expires_at) : null
  const expiresSoon = !!expires && expires.getTime() - Date.now() < 30 * 24 * 3600 * 1000
  // The account line: Claude has only a token tail, Codex knows the email/plan.
  const accountBits = [
    status?.email,
    status?.plan ? status.plan.replace(/_/g, ' ') : null,
    status?.token_tail ? `token …${status.token_tail}` : null,
  ].filter(Boolean)

  return (
    <div style={{ marginBottom: 4 }}>
      {isSet && !flow && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '8px 10px',
            borderRadius: 6,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <CheckCircle2 size={15} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
            <span>
              <strong>Connected</strong>
              {accountBits.length ? ` · ${accountBits.join(' · ')}` : ''}
              {expires ? (
                <span
                  style={{
                    color: expiresSoon ? 'var(--color-warning, #b45309)' : 'var(--color-text-secondary)',
                  }}
                >
                  {' '}· expires {expires.toLocaleDateString()}
                  {expiresSoon ? ' — reconnect soon' : ''}
                </span>
              ) : null}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleConnect} disabled={starting}>
              {starting ? 'Starting…' : 'Reconnect'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleDisconnect}
              disabled={disconnecting}
              title="Disconnect"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Unplug size={13} />
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        </div>
      )}

      {!isSet && !flow && (
        <button type="button" className="btn btn-primary btn-sm" onClick={handleConnect} disabled={starting}>
          {starting ? 'Starting…' : `Connect ${provider} account`}
        </button>
      )}

      {flow && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 6,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            <strong>1.</strong> A {provider} sign-in tab was opened — sign in
            {api.kind === 'paste' ? ' and approve access.' : '.'}{' '}
            <a
              href={flow.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
            >
              Re-open the sign-in page <ExternalLink size={11} />
            </a>
            <br />
            {api.kind === 'device' ? (
              <>
                <strong>2.</strong> Enter this one-time code on that page:
              </>
            ) : (
              <>
                <strong>2.</strong> {provider} will show you an <strong>authorization code</strong> — paste it
                here:
              </>
            )}
          </div>

          {api.kind === 'device' ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <code
                  style={{
                    fontFamily: 'var(--font-mono, monospace)',
                    fontSize: 21,
                    letterSpacing: 2,
                    fontWeight: 600,
                    padding: '6px 12px',
                    borderRadius: 6,
                    background: 'var(--color-bg-secondary)',
                    border: '1px solid var(--color-border)',
                    userSelect: 'all',
                  }}
                >
                  {flow.user_code}
                </code>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={copyCode}
                  title="Copy the code"
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Copy size={13} />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--color-text-secondary)',
                }}
              >
                <Loader2 size={13} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                Waiting for you to approve the code…
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="input"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleComplete()
                }}
                placeholder="Paste the authorization code…"
                style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13, flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleComplete}
                disabled={completing || !code.trim()}
                style={{ minWidth: 90 }}
              >
                {completing ? 'Connecting…' : 'Connect'}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleCancel} disabled={completing}>
                Cancel
              </button>
            </div>
          )}

          {footer && <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{footer}</div>}
        </div>
      )}
    </div>
  )
}
